import React from 'react';
import classNames from 'classnames';

import { menuModalOpen } from '@rikstv/play-common/src/forces/ui/ui.slice';
import { useIsAuthenticated } from '@rikstv/play-common/src/hooks/useIsAuthenticated';
import { SearchIcon, SettingsIcon } from '@rikstv/play-common/src/icons';
import { Link } from '@rikstv/play-common/src/router/Link';
import { commonRoutes } from '@rikstv/play-common/src/router/router.path';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { useFeatureFlag } from '@rikstv/play-common/src/utils/featureFlags';

import { useRiksTvDispatch } from '../../../root.reducer';
import routerPath, { routerPaths } from '../../router/router.path';
import { RTVLogo } from '../rtvLogo/RTVLogo';

import { Login, MyPage, NewCustomer } from './Header';

import style from './Header.module.css';

const LoginButton = () => (
  <Link
    className={classNames(style.buttonMenu, style.light)}
    to="#login"
    onClick={e => {
      e.preventDefault();
      authService.login();
    }}>
    Logg inn
  </Link>
);
const LogoutButton = () => (
  <Link
    className={classNames(style.buttonMenu, style.light)}
    to="#logout"
    onClick={e => {
      e.preventDefault();
      authService.logout();
    }}>
    Logg ut
  </Link>
);

export const LoginLogoutButton = () => {
  const isLoggedIn = useIsAuthenticated();
  if (isLoggedIn) {
    return <LogoutButton />;
  } else {
    return <LoginButton />;
  }
};
// fjerne denne
export const BackMenuItem = () => {
  return (
    <Link
      className={classNames(style.buttonMenu, style.light)}
      to="https://www.rikstv.no/"
      title="Tilbake til RiksTV.no">
      <i />
      RiksTV.no
    </Link>
  );
};

export const SettingsMenuItem = React.memo(({ isMobile = false }: { isMobile?: boolean }) => (
  <li>
    <MenuButton
      to={routerPath.settings()}
      className={classNames(style.buttonWithIcon, 'with-keyboard-focus', { [style.light]: isMobile })}
      title="Gå til innstillinger"
      data-testid="menu-settingsicon">
      Innstillinger
    </MenuButton>
  </li>
));

export const SettingsMenuIconItem = React.memo(({ isMobile = false }: { isMobile?: boolean }) => (
  <li>
    <MenuButton
      to={routerPath.settings()}
      className={classNames(style.buttonWithIcon, 'with-keyboard-focus', { [style.light]: isMobile })}
      title="Gå til innstillinger"
      data-testid="menu-settingsicon">
      <SettingsIcon className={style.buttonIcon} />
      <span className="sr-only">Innstillinger</span>
    </MenuButton>
  </li>
));

export const SearchMenuItem = React.memo(() => (
  <li>
    <MenuButton
      to={routerPath.search()}
      className={classNames(style.buttonWithIcon, 'with-keyboard-focus')}
      title="Finn noe å se på"
      data-testid="menu-searchicon">
      <SearchIcon ariaHidden={true} className={style.buttonIcon} />
      <span className="sr-only">Søk</span>
    </MenuButton>
  </li>
));

export const MobileMenuBar = () => {
  const isLoggedIn = useIsAuthenticated();
  const dispatch = useRiksTvDispatch();
  const handleMobileMenuClick = () => dispatch(menuModalOpen());
  return (
    <div className={style.mobile}>
      <div className={style.mobileButton}>
        <button
          onClick={handleMobileMenuClick}
          className={`with-keyboard-focus ${style.buttonMenuMobile}`}
          title="Åpne meny">
          <span />
        </button>
      </div>
      <RTVLogo className={style.mobileRtvLogo} />
      <nav className={style.mobileNav}>
        <ul style={{ display: 'flex', alignItems: 'center' }}>
          <SearchMenuItem />
          {isLoggedIn ? <MyPage /> : <NewCustomer />}
          {!isLoggedIn ? <Login /> : ''}
        </ul>
      </nav>
    </div>
  );
};

export const LiveMenuItem = ({ isMobile }: { isMobile?: boolean }) => {
  return (
    <li>
      <MenuButton
        className={classNames('with-keyboard-focus', { [style.light]: isMobile })}
        to={commonRoutes.watchLive()}>
        Direkte
      </MenuButton>
    </li>
  );
};

export const TvGuideMenuItem = ({ isMobile }: { isMobile?: boolean }) => {
  return (
    <li>
      <MenuButton className={classNames('with-keyboard-focus', { [style.light]: isMobile })} to={routerPaths.tvGuide}>
        TV-guide
      </MenuButton>
    </li>
  );
};

export const OpptakMenuItem = ({ isMobile }: { isMobile?: boolean }) => {
  const [npvrEnabled] = useFeatureFlag('enable-npvr');
  if (!npvrEnabled) return null;

  return (
    <li>
      <MenuButton className={classNames('with-keyboard-focus', { [style.light]: isMobile })} to={routerPaths.opptak}>
        Opptak
      </MenuButton>
    </li>
  );
};

export const MenuButton = React.memo(({ to, className, children, ...props }: Parameters<typeof Link>[0]) => (
  <Link
    to={to}
    className={classNames(style.buttonMenu, className)}
    activeClassName={style.buttonSelected}
    withDefaultStyle={false}
    scrollToTopOnReclick
    {...props}>
    {children}
  </Link>
));
